import {Component, Inject, OnDestroy} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {NGXLogger} from 'ngx-logger';
import {Subject, takeUntil, timeout} from 'rxjs';
// OpenAPI Shell Service
import {
  EulaService as ShellApiEulaService,
  EulaApprovalDTO,
} from '../../openapi/shell-service-openapi';
import {clearReloadCount} from '../../privacy-policy-content/privacy-policy-reload-count';
import {PrivacyPolicyVersionMatch} from '../../privacy-policy-content/privacy-policy-state';
import {PRIVACY_POLICY_VERSION} from '../../privacy-policy-content/privacy-policy-version';


export interface EulaPopupData {
  readonly versionMatch: PrivacyPolicyVersionMatch;
}

/**
 * Wie der Dialog ausgegangen ist. Fehlt das Ergebnis, wurde der Dialog zerstört, etwa durch eine
 * Navigation - der Guard unternimmt dann nichts und öffnet keine neue Schranke.
 */
export type EulaPopupResult = 'approved' | 'reauthenticate' | 'forbidden';

/**
 * Nach dieser Zeit gilt die Anfrage als gescheitert.
 *
 * Ohne Grenze bliebe der Dialog bildschirmfüllend mit gesperrtem Knopf stehen: Der generierte
 * Dienst bringt kein Zeitlimit mit, und die Interceptor-Kette ergänzt keines. Großzügiger als die
 * zehn Sekunden in StandaloneService.requestAccessToken, weil eine abgebrochene Zustimmung mehr
 * kostet als Wartezeit und über Mobilfunk die kleinere Grenze schnell erreicht ist.
 */
const APPROVAL_TIMEOUT_MS = 30_000;


/**
 * Die Zutrittsschranke mit der Datenschutzerklärung.
 *
 * Der Dialog schickt die Zustimmung selbst ab und bleibt offen, bis der Server geantwortet hat:
 * Nur so hat eine Ablehnung wegen abweichender Fassung einen Ort, an dem sie sich zeigen kann, und
 * ein Fehler öffnet keine neue Schranke.
 */
@Component({
  selector: 'app-eula-popup',
  templateUrl: './eula-popup.component.html',
  styleUrls: ['./eula-popup.component.scss']
})
export class EulaPopupComponent
  implements OnDestroy {

  versionMatch: PrivacyPolicyVersionMatch;

  busy = false;

  private readonly unsubscribe$ = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<EulaPopupComponent, EulaPopupResult>,
    private logger: NGXLogger,
    private snackBar: MatSnackBar,
    private shellApiEulaService: ShellApiEulaService,
    @Inject(MAT_DIALOG_DATA) data: EulaPopupData,
  ) {
    this.versionMatch = data.versionMatch;

    // INFO: Nicht erst bei der Zerstörung, die kommt nach der Animation: Material setzt das
    // Ergebnis schon bei close(), und ein close() aus einer verspäteten Antwort überschriebe es -
    // der Guard navigierte dann, obwohl der Dialog ergebnislos verschwunden ist.
    this.dialogRef.beforeClosed()
      .subscribe(() => this.stopRequest());
  }

  ngOnDestroy(): void {
    this.stopRequest();
    this.unsubscribe$.complete();
  }

  private stopRequest(): void {
    this.unsubscribe$.next();
  }

  approved(
    approved: boolean,
  ): void {
    if (!approved || this.busy) {
      return;
    }
    this.writeApproval();
  }

  private writeApproval(): void {
    this.busy = true;

    const data: EulaApprovalDTO = {
      // INFO: Die Fassung, die dieser Client anzeigt, nicht die vom Server gemeldete - sonst
      // bescheinigte die Zustimmung womöglich eine Fassung, die der Nutzer nie sah.
      version: PRIVACY_POLICY_VERSION,
      approved: true,
    };

    this.shellApiEulaService.writeApproval(data)
      .pipe(
        timeout(APPROVAL_TIMEOUT_MS),
        takeUntil(this.unsubscribe$),
      )
      .subscribe({
        next: value => {
          this.logger.info('EULA approval successful', value);

          // INFO: Erst eine gespeicherte Zustimmung belegt, dass die Abweichung behoben ist. Beim
          // bloßen Zusammenpassen der Fassungen zu löschen begänne der Zähler jedes Mal von vorn
          // und erreichte die Grenze nie.
          clearReloadCount();
          this.dialogRef.close('approved');
        },
        error: error => {
          this.busy = false;
          this.handleError(error);
        },
      });
  }

  private handleError(
    error: unknown,
  ): void {
    const status = error instanceof HttpErrorResponse ? error.status : 0;

    // INFO: Beide gleich behandelt, solange offen ist, mit welchem Status der Server den
    // Fassungskonflikt meldet. Ein 400 aus fehlenden Pflichtangaben kann hier nicht auftreten,
    // und tut er es doch, ist Neuladen die beste Auskunft.
    if (status === 409 || status === 400) {
      this.logger.warn('EULA approval rejected, version is not the current one', error);
      this.versionMatch = 'deviates';
      return;
    }

    // INFO: Weder der AuthInterceptor noch die OIDC-Kette behandeln diese beiden Antworten, und
    // der Anmeldeguard läuft bei einer HTTP-Fehlerantwort nicht erneut. Ohne benannten Übergang
    // bliebe nach dem geschlossenen Dialog kein definierter Zustand.
    if (status === 401) {
      this.logger.warn('EULA approval unauthorized, asking for a new sign-in', error);
      this.dialogRef.close('reauthenticate');
      return;
    }
    if (status === 403) {
      this.logger.warn('EULA approval forbidden', error);
      this.dialogRef.close('forbidden');
      return;
    }

    // INFO: Netzwerkfehler, Serverfehler, Zeitlimit: Der Dialog bleibt offen und der Knopf wird
    // wieder frei - ein erneuter Versuch kann helfen.
    this.logger.warn('EULA approval failed', error);
    this.snackBar.open('Es ist ein Fehler aufgetreten, bitte versuche es erneut', undefined, {
        duration: 5000,
        panelClass: 'error'
      },
    );
  }
}
